import React, { useEffect, useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, RefreshCw, LogOut } from 'lucide-react';
import { AuthContext } from '../../contexts/AuthContext';

const WarningDialog = ({ onClose, onExtendSession, timeoutMinutes = 5, onTimeout }) => {
  const { t } = useTranslation();
  const { logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [remainingSeconds, setRemainingSeconds] = useState(timeoutMinutes * 60);

  const handleLogout = () => {
    logout();
    navigate('/');
    onClose();
  };

  const handleKeyPress = useCallback((event) => {
    if (event.key === 'Escape') {
      onExtendSession(); // Default to extending session on Escape
    }
  }, [onExtendSession]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyPress);
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [handleKeyPress]);

  // Countdown timer
  useEffect(() => {
    const interval = setInterval(() => {
      setRemainingSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          if (onTimeout) {
            onTimeout();
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [onTimeout]);

  // Format time as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-950/40 p-4 dark:bg-black/70">
      <div className="w-full max-w-md overflow-hidden rounded-xl bg-white shadow-overlay ring-1 ring-gray-900/5 animate-slide-up dark:bg-gray-800 dark:ring-white/10">
        <div className="flex items-center gap-2.5 px-6 pb-2 pt-5">
          <AlertTriangle className="h-5 w-5 flex-shrink-0 text-warning-600 dark:text-warning-400" strokeWidth={1.75} />
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-50">
            {t('Session Timeout Warning')}
          </h2>
        </div>
        <div className="space-y-4 px-6 pb-6 pt-1">
          <p className="text-sm leading-relaxed text-gray-600 dark:text-gray-300">
            {t('You will be logged out due to inactivity. Do you want to extend your session?')}
          </p>
          <div className="flex items-center justify-center rounded-lg border border-gray-200/70 bg-gray-50 py-4 dark:border-white/[0.07] dark:bg-white/[0.03]">
            <div className="text-4xl font-semibold tabular-nums tracking-tight text-danger-600 dark:text-danger-400">
              {formatTime(remainingSeconds)}
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 border-t border-gray-100 bg-gray-50/70 px-6 py-3.5 dark:border-gray-700 dark:bg-gray-900/40">
          <button
            onClick={handleLogout}
            className="btn btn-secondary"
          >
            <LogOut className="h-4 w-4" />
            {t('Logout')}
          </button>
          <button
            onClick={onExtendSession}
            className="btn btn-primary"
          >
            <RefreshCw className="h-4 w-4" />
            {t('Extend Session')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WarningDialog;